/**
 * Session Warning Modal
 * 
 * Displays a countdown warning when the session is about to expire.
 * Shows time remaining, allows session refresh, and provides logout option.
 */

import React, { useState } from 'react';
import {
  Modal,
  Backdrop,
  Fade,
  Box,
  Typography,
  Button,
  LinearProgress,
  Stack,
  useTheme,
  IconButton,
  Alert
} from '@mui/material';
import {
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Logout as LogoutIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useSessionWarning } from '../../contexts/SessionWarningContext';
import { formatTimeRemaining, getUrgencyLevel } from '../../utils/timeFormatters';
// import { logger } from "../../utils/logger";
const SessionWarningModal: React.FC = () => {
  const theme = useTheme();
  const {
    isWarningVisible,
    timeRemaining,
    isExpiring,
    refreshSession,
    dismissWarning
  } = useSessionWarning();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  // Don't render if warning is not visible
  if (!isWarningVisible || timeRemaining === null) {
    return null;
  }

  const urgency = getUrgencyLevel(timeRemaining);
  const formattedTime = formatTimeRemaining(timeRemaining);
  const progressValue = Math.max(0, Math.min(100, (timeRemaining / (5 * 60 * 1000)) * 100));

  // Get color based on urgency
  const getColor = () => {
    switch (urgency) {
      case 'critical':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      default:
        return theme.palette.info.main;
    }
  };

  const color = getColor();

  // Handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    setRefreshError(null);

    try {
      const success = await refreshSession();
      
      if (!success) {
        setRefreshError('Failed to refresh session. Please try again.');
      }
    } catch (error) {
      setRefreshError('An error occurred while refreshing your session.');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    window.location.href = '/logout';
  };

  // Handle dismiss (only if > 3 minutes)
  const handleDismiss = () => {
    if (timeRemaining > 3 * 60 * 1000) {
      dismissWarning();
    }
  };

  return (
    <Modal
      open={isWarningVisible}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)'
        }
      }}
      disableEscapeKeyDown={isExpiring}
      onClose={handleDismiss}
      aria-labelledby="session-warning-title"
      aria-describedby="session-warning-description"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9998 // Below session expired modal (9999)
      }}
    >
      <Fade in={isWarningVisible}>
        <Box
          sx={{
            position: 'relative',
            width: { xs: '90%', sm: 500 },
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: `0 8px 32px ${color}40`,
            p: 4,
            outline: 'none',
            border: `2px solid ${color}`,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '6px',
              background: `linear-gradient(90deg, ${color} 0%, ${color}80 100%)`,
              borderRadius: '12px 12px 0 0'
            }
          }}
        >
          {/* Close button (only show if not critical) */}
          {!isExpiring && (
            <IconButton
              onClick={handleDismiss}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'text.secondary'
              }}
              aria-label="Dismiss warning"
            >
              <CloseIcon />
            </IconButton>
          )}

          <Stack spacing={3} alignItems="center">
            {/* Warning Icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${color}15`,
                animation: isExpiring ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%, 100%': {
                    transform: 'scale(1)',
                    opacity: 1
                  },
                  '50%': {
                    transform: 'scale(1.05)',
                    opacity: 0.8
                  }
                }
              }}
            >
              <WarningIcon sx={{ fontSize: 48, color }} />
            </Box>

            {/* Title */}
            <Typography
              id="session-warning-title"
              variant="h4"
              component="h2"
              textAlign="center"
              sx={{
                fontWeight: 700,
                color: color,
                lineHeight: 1.2
              }}
            >
              Session Expiring Soon
            </Typography>

            {/* Countdown */}
            <Box textAlign="center">
              <Typography
                variant="h2"
                component="div"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  color: color,
                  letterSpacing: '-0.02em',
                  fontSize: { xs: '3rem', sm: '4rem' }
                }}
                aria-live="polite"
                aria-atomic="true"
              >
                {formattedTime}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Time remaining
              </Typography>
            </Box>

            {/* Progress Bar */}
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={progressValue}
                sx={{
                  height: 8,
                  borderRadius: 1,
                  bgcolor: `${color}20`,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: color,
                    borderRadius: 1,
                    transition: 'transform 1s linear'
                  }
                }}
              />
            </Box>

            {/* Message */}
            <Typography
              id="session-warning-description"
              variant="body1"
              textAlign="center"
              color="text.secondary"
              sx={{ maxWidth: 400 }}
            >
              Your session will expire soon. Click "Stay Logged In" to extend your session,
              or you will be automatically logged out.
            </Typography>

            {/* Error Message */}
            {refreshError && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {refreshError}
              </Alert>
            )}

            {/* Action Buttons */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ width: '100%', mt: 2 }}
            >
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={isRefreshing}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${color}dd 0%, ${color}bb 100%)`,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 20px ${color}40`
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {isRefreshing ? 'Refreshing...' : 'Stay Logged In'}
              </Button>

              <Button
                variant="outlined"
                size="large"
                fullWidth
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                disabled={isRefreshing}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderColor: color,
                  color: color,
                  '&:hover': {
                    borderColor: color,
                    bgcolor: `${color}10`,
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Logout
              </Button>
            </Stack>

            {/* Hint */}
            <Typography variant="caption" color="text.secondary" textAlign="center">
              Tip: Any activity will automatically extend your session
            </Typography>
          </Stack>
        </Box>
      </Fade>
    </Modal>
  );
};

export default SessionWarningModal;

